import ReportSectionCard from './shared/ReportSectionCard.component'
import ReportSectionHeading from '@components/pages/Reports/shared/ReportSectionHeading.component'
import React, { useMemo, useState } from 'react'
import ReportViewTabs from '@components/pages/Reports/shared/ReportViewTabs.component'
import DashboardSectionEmptyState from '@components/pages/Dashboard/DashboardSectionEmptyState.component'
import ReportDataTable, {
  ReportDataTableColumn,
} from '@components/pages/Reports/shared/ReportDataTable.component'
import ReportFullViewTableTitle, {
  useReportFullViewSection,
} from '@components/pages/Reports/shared/ReportFullViewTableTitle.component'
import ReportExpandableSection from '@components/pages/Reports/shared/ReportExpandableSection.component'
import ReportBundleStickyTopWhenExpanded from '@components/pages/Reports/shared/ReportBundleStickyTopWhenExpanded.component'
import ReportFilterToolbar from '@components/pages/Reports/shared/ReportFilterToolbar.component'
import ReportKpiGrid from '@components/pages/Reports/shared/ReportKpiGrid.component'
import ReportPageHeader from '@components/pages/Reports/shared/ReportPageHeader.component'
import { REPORTS_DISTRIBUTION_VIEW_TABS } from '@constants/Reports/patientBaseHealth.constants'
import {
  EFFICIENCY_SCORECARD_FILTERS,
  EFFICIENCY_SCORECARD_KPI_DEFINITIONS,
  type ProviderFinancialRow,
} from '@constants/Reports/efficiencyScorecard.constants'
import {
  ReportsDistributionView,
  ReportsKpiMetric,
} from '@dataTypes/reports.type'
import { useEfficiencyScorecard } from '@hooks/reports/useEfficiencyScorecard'
import { useReportFinanceUiEffects } from '@hooks/reports/useReportFinanceUiEffects'
import type { ReportPeriodBucket } from '@services/api/reports/finance.api'
import {
  hasNonZeroDashboardValues,
  shouldShowDashboardSectionEmpty,
} from '@utils/dashboardSectionEmpty.util'
import {
  computeDynamicDollarAxis,
  formatKpiCurrency,
  formatKpiPercent,
} from '@utils/reports/revenueRealizationFinance.util'
import {
  Bar,
  BarChart,
  CartesianGrid,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from '@components/pages/Reports/shared/ReportDesignSystemCharts.component'

const REPORT_CHART_AXIS_TICK = { fontSize: 12, fill: 'var(--gray-500)' }
const REPORT_CHART_TOOLTIP_STYLE = {
  borderRadius: 8,
  border: '1px solid var(--gray-200)',
  fontSize: 12,
}
const REPORT_CHART_GRID_STROKE = 'var(--gray-100)'
const REPORT_BAR_CHART_MARGIN = { top: 24, right: 12, left: 4, bottom: 0 }
const REPORT_BAR_CATEGORY_GAP = '16%'
const REPORT_YAXIS_WIDTH = 56
const REPORT_PERCENTAGE_Y_AXIS = {
  domain: [0, 100] as [number, number],
  ticks: [0, 20, 40, 60, 80, 100],
  tickFormatter: (v: number) => `${v}%`,
}

function formatDollarLabel(value: number): string {
  if (value >= 10_000) {
    const inK = value / 1000
    const rounded = Math.round(inK)
    return `$${rounded}K`
  }
  if (value >= 1000) {
    const inK = value / 1000
    const rounded = Math.round(inK * 100) / 100
    return `$${rounded}K`
  }
  return `$${value}`
}

function formatDollarTooltip(value: number): string {
  return `$${value.toLocaleString('en-US')}`
}

function formatCurrencyPerHour(value: number | undefined): string {
  const formatted = formatKpiCurrency(value)
  if (formatted === '—') return formatted
  return `${formatted}/hr`
}

function formatTableCurrency(value: number): string {
  return `$${Math.round(value).toLocaleString('en-US')}`
}

function buildDollarSeries(
  rows: ReportPeriodBucket[],
  view: ReportsDistributionView,
): Array<{ month: string; barValue: number; topLabel: string }> {
  if (view === 'Count') {
    return rows.map((row) => ({
      month: row.label,
      barValue: row.value,
      topLabel: formatDollarLabel(row.value),
    }))
  }
  const total = rows.reduce((sum, r) => sum + r.value, 0) || 1
  return rows.map((row) => {
    const pct = Math.round((row.value / total) * 1000) / 10
    return {
      month: row.label,
      barValue: pct,
      topLabel: `${pct}%`,
    }
  })
}

function EfficiencyScorecardReport(): React.ReactElement {
  const [efficiencyView, setEfficiencyView] =
    useState<ReportsDistributionView>('Count')
  const [productionPerHourView, setProductionPerHourView] =
    useState<ReportsDistributionView>('Count')
  const [reportSaveViewAction, setReportSaveViewAction] =
    useState<JSX.Element | null>(null)
  const [reportGeneratePDFAction, setReportGeneratePDFAction] =
    useState<JSX.Element | null>(null)
  // `reportFiltersAndMetrics` is the source of truth for the filters and metrics of the currently selected report view.
  // Use this for all data queries and any logic that depends on the selected report view.
  const [reportFiltersAndMetrics, setReportFiltersAndMetrics] = useState<{
    filters: Record<string, boolean>
    metrics: Record<string, boolean>
  } | null>(null)
  const providerComparisonFullView = useReportFullViewSection('eff-providers')

  const { loading, error, data, bucketLabel } = useEfficiencyScorecard(
    (reportFiltersAndMetrics?.filters ?? {}) as unknown as Record<
      string,
      string
    >,
  )
  useReportFinanceUiEffects({
    loading,
    error,
    errorMessage: 'Failed to load efficiency scorecard data. Please try again.',
  })

  const kpiItems = useMemo((): ReportsKpiMetric[] => {
    if (!data) {
      return EFFICIENCY_SCORECARD_KPI_DEFINITIONS.map((def) => ({
        id: def.id,
        title: def.title,
        footer: def.footer,
        value: loading ? '…' : '—',
      }))
    }

    const valueById: Record<string, number> = {
      'efficiency-score': data.efficiencyScore.value,
      'production-per-hour': data.productionPerHour.value,
      'chair-utilization': data.chairUtilization.value,
      'total-production': data.totalProduction.value,
    }

    return EFFICIENCY_SCORECARD_KPI_DEFINITIONS.map((def) => {
      const raw = valueById[def.id]
      let value: string
      if (def.format === 'currencyPerHour') {
        value = formatCurrencyPerHour(raw)
      } else if (def.format === 'percent') {
        value = formatKpiPercent(raw)
      } else {
        value = formatKpiCurrency(raw)
      }
      return {
        id: def.id,
        title: def.title,
        footer: def.footer,
        value,
      }
    })
  }, [data, loading])

  const efficiencyChartData = useMemo(
    () =>
      buildDollarSeries(data?.efficiencyScore.buckets ?? [], efficiencyView),
    [data, efficiencyView],
  )
  const efficiencyYAxis = useMemo(() => {
    if (efficiencyView === 'Percentage') return REPORT_PERCENTAGE_Y_AXIS
    return computeDynamicDollarAxis(
      efficiencyChartData.map((row) => row.barValue),
    )
  }, [efficiencyView, efficiencyChartData])

  const productionPerHourChartData = useMemo(
    () =>
      buildDollarSeries(
        data?.productionPerHour.buckets ?? [],
        productionPerHourView,
      ),
    [data, productionPerHourView],
  )
  const productionPerHourYAxis = useMemo(() => {
    if (productionPerHourView === 'Percentage') return REPORT_PERCENTAGE_Y_AXIS
    return computeDynamicDollarAxis(
      productionPerHourChartData.map((row) => row.barValue),
    )
  }, [productionPerHourView, productionPerHourChartData])

  const chairUtilizationData = useMemo(
    () =>
      (data?.chairUtilization.buckets ?? []).map((row) => ({
        month: row.label,
        barValue: row.value,
        topLabel: `${row.value}%`,
      })),
    [data],
  )
  const chairUtilizationYAxis = useMemo(() => REPORT_PERCENTAGE_Y_AXIS, [])

  const providerRows = useMemo((): ProviderFinancialRow[] => {
    if (!data?.providers?.length) return []
    return data.providers.map((row) => ({
      id: row.providerId,
      provider: row.providerName,
      production: formatTableCurrency(row.production),
      productionPerHour: formatTableCurrency(row.productionPerHour),
      efficiencyScore: formatTableCurrency(row.efficiencyScore),
      chairUtilization: `${row.chairUtilization}%`,
    }))
  }, [data])

  const providerColumns: ReportDataTableColumn<ProviderFinancialRow>[] =
    useMemo(
      () => [
        { key: 'provider', label: 'Provider' },
        {
          key: 'production',
          label: 'Production',
          headerClassName: 'reports-efficiency-scorecard__table-head--num',
          cellClassName: 'reports-efficiency-scorecard__table-cell--num',
        },
        {
          key: 'productionPerHour',
          label: 'Prod/Hour',
          headerClassName: 'reports-efficiency-scorecard__table-head--num',
          cellClassName: 'reports-efficiency-scorecard__table-cell--num',
        },
        {
          key: 'efficiencyScore',
          label: 'Efficiency Score',
          headerClassName: 'reports-efficiency-scorecard__table-head--num',
          cellClassName: 'reports-efficiency-scorecard__table-cell--num',
        },
        {
          key: 'chairUtilization',
          label: 'Chair Utilization',
          headerClassName: 'reports-efficiency-scorecard__table-head--num',
          cellClassName: 'reports-efficiency-scorecard__table-cell--num',
        },
      ],
      [],
    )

  const renderDollarChart = (
    chartData: ReturnType<typeof buildDollarSeries>,
    axis: ReturnType<typeof computeDynamicDollarAxis>,
    view: ReportsDistributionView,
  ): React.ReactElement => (
    <ResponsiveContainer width="100%" height="100%">
      <BarChart
        data={chartData}
        margin={REPORT_BAR_CHART_MARGIN}
        barCategoryGap={REPORT_BAR_CATEGORY_GAP}
      >
        <CartesianGrid
          vertical={false}
          strokeDasharray="0"
          stroke={REPORT_CHART_GRID_STROKE}
        />
        <XAxis
          dataKey="month"
          axisLine={false}
          tickLine={false}
          tick={REPORT_CHART_AXIS_TICK}
        />
        <YAxis
          domain={axis.domain}
          ticks={axis.ticks}
          tickFormatter={axis.tickFormatter}
          axisLine={false}
          tickLine={false}
          tick={REPORT_CHART_AXIS_TICK}
          width={REPORT_YAXIS_WIDTH}
        />
        <Tooltip
          formatter={(value: number) =>
            view === 'Count' ? formatDollarTooltip(value) : `${value}%`
          }
          contentStyle={REPORT_CHART_TOOLTIP_STYLE}
        />
        <Bar
          dataKey="barValue"
          fill="var(--blue-400)"
          radius={[8, 8, 8, 8]}
          barSize={100}
        />
      </BarChart>
    </ResponsiveContainer>
  )

  const renderPercentChart = (
    chartData: Array<{ month: string; barValue: number; topLabel: string }>,
    axis: {
      domain: [number, number]
      ticks: number[]
      tickFormatter: (v: number) => string
    },
  ): React.ReactElement => (
    <ResponsiveContainer width="100%" height="100%">
      <BarChart
        data={chartData}
        margin={REPORT_BAR_CHART_MARGIN}
        barCategoryGap={REPORT_BAR_CATEGORY_GAP}
      >
        <CartesianGrid
          vertical={false}
          strokeDasharray="0"
          stroke={REPORT_CHART_GRID_STROKE}
        />
        <XAxis
          dataKey="month"
          axisLine={false}
          tickLine={false}
          tick={REPORT_CHART_AXIS_TICK}
        />
        <YAxis
          domain={axis.domain}
          ticks={axis.ticks}
          tickFormatter={axis.tickFormatter}
          axisLine={false}
          tickLine={false}
          tick={REPORT_CHART_AXIS_TICK}
          width={REPORT_YAXIS_WIDTH}
        />
        <Tooltip
          formatter={(value: number) => `${value}%`}
          contentStyle={REPORT_CHART_TOOLTIP_STYLE}
        />
        <Bar
          dataKey="barValue"
          fill="var(--blue-400)"
          radius={[8, 8, 8, 8]}
          barSize={100}
        />
      </BarChart>
    </ResponsiveContainer>
  )

  const kpiEmpty = shouldShowDashboardSectionEmpty(
    loading,
    hasNonZeroDashboardValues(
      data?.efficiencyScore.value,
      data?.productionPerHour.value,
      data?.chairUtilization.value,
      data?.totalProduction.value,
    ),
  )
  const efficiencyEmpty = shouldShowDashboardSectionEmpty(
    loading,
    (data?.efficiencyScore?.buckets ?? []).some((row) =>
      hasNonZeroDashboardValues(row.value),
    ),
  )
  const productionPerHourEmpty = shouldShowDashboardSectionEmpty(
    loading,
    (data?.productionPerHour?.buckets ?? []).some((row) =>
      hasNonZeroDashboardValues(row.value),
    ),
  )
  const chairUtilizationEmpty = shouldShowDashboardSectionEmpty(
    loading,
    (data?.chairUtilization?.buckets ?? []).some((row) =>
      hasNonZeroDashboardValues(row.value),
    ),
  )
  const providersEmpty = shouldShowDashboardSectionEmpty(
    loading,
    (data?.providers ?? []).some((row) =>
      hasNonZeroDashboardValues(
        row.production,
        row.productionPerHour,
        row.efficiencyScore,
        row.chairUtilization,
      ),
    ),
  )

  return (
    <div className="reports-patient-base-health reports-treatment-conversion-funnel reports-efficiency-scorecard">
      <ReportBundleStickyTopWhenExpanded>
        <ReportPageHeader title="Efficiency Scorecard">
          {reportSaveViewAction}
          {reportGeneratePDFAction}
        </ReportPageHeader>

        <ReportFilterToolbar
          filters={EFFICIENCY_SCORECARD_FILTERS}
          onReportSaveViewActionChange={setReportSaveViewAction}
          onReportGeneratePDFActionChange={setReportGeneratePDFAction}
          onReportFiltersAndMetricsChange={setReportFiltersAndMetrics}
        />
      </ReportBundleStickyTopWhenExpanded>

      <div className="reports-treatment-conversion-funnel__stack">
        <ReportExpandableSection sectionId="eff-upper">
          {kpiEmpty ? (
            <DashboardSectionEmptyState />
          ) : (
            <ReportKpiGrid items={kpiItems} />
          )}

          <div className="reports-treatment-conversion-funnel__grid">
            <ReportSectionCard className="reports-patient-base-health__chart-card">
              <div className="reports-patient-value-revenue__column">
                <div className="reports-patient-base-health__chart-head">
                  <ReportSectionHeading>
                    Efficiency Score ({bucketLabel})
                  </ReportSectionHeading>
                  {efficiencyEmpty ? null : (
                    <ReportViewTabs
                      options={REPORTS_DISTRIBUTION_VIEW_TABS}
                      value={efficiencyView}
                      onChange={(value) =>
                        setEfficiencyView(value as ReportsDistributionView)
                      }
                      className="reports-patient-base-health__view-group"
                    />
                  )}
                </div>
                {efficiencyEmpty ? (
                  <DashboardSectionEmptyState className="dashboard-section-empty--embed" />
                ) : (
                  <div className="reports-treatment-conversion-funnel__chart">
                    {renderDollarChart(
                      efficiencyChartData,
                      efficiencyYAxis,
                      efficiencyView,
                    )}
                  </div>
                )}
              </div>
            </ReportSectionCard>

            <ReportSectionCard className="reports-patient-base-health__chart-card">
              <div className="reports-patient-value-revenue__column">
                <div className="reports-patient-base-health__chart-head">
                  <ReportSectionHeading>
                    Production per Hour ({bucketLabel})
                  </ReportSectionHeading>
                  {productionPerHourEmpty ? null : (
                    <ReportViewTabs
                      options={REPORTS_DISTRIBUTION_VIEW_TABS}
                      value={productionPerHourView}
                      onChange={(value) =>
                        setProductionPerHourView(
                          value as ReportsDistributionView,
                        )
                      }
                      className="reports-patient-base-health__view-group"
                    />
                  )}
                </div>
                {productionPerHourEmpty ? (
                  <DashboardSectionEmptyState className="dashboard-section-empty--embed" />
                ) : (
                  <div className="reports-treatment-conversion-funnel__chart">
                    {renderDollarChart(
                      productionPerHourChartData,
                      productionPerHourYAxis,
                      productionPerHourView,
                    )}
                  </div>
                )}
              </div>
            </ReportSectionCard>
          </div>
        </ReportExpandableSection>

        <div className="reports-treatment-conversion-funnel__grid reports-efficiency-scorecard__split-grid">
          <ReportExpandableSection sectionId="eff-chair">
            <ReportSectionCard className="reports-patient-base-health__chart-card">
              <div className="reports-patient-value-revenue__column">
                <div className="reports-patient-base-health__chart-head">
                  <ReportSectionHeading>
                    Chair Utilization ({bucketLabel})
                  </ReportSectionHeading>
                </div>
                {chairUtilizationEmpty ? (
                  <DashboardSectionEmptyState className="dashboard-section-empty--embed" />
                ) : (
                  <div className="reports-treatment-conversion-funnel__chart">
                    {renderPercentChart(
                      chairUtilizationData,
                      chairUtilizationYAxis,
                    )}
                  </div>
                )}
              </div>
            </ReportSectionCard>
          </ReportExpandableSection>

          <ReportExpandableSection sectionId="eff-providers">
            <ReportSectionCard className="reports-patient-base-health__chart-card">
              <div className="reports-patient-value-revenue__column reports-patient-value-revenue__column--fill">
                <div className="reports-patient-base-health__chart-head">
                  <ReportFullViewTableTitle
                    sectionId="eff-providers"
                    title="Provider Financial Comparison"
                    disableEnter={providersEmpty}
                  />
                </div>
                {providersEmpty ? (
                  <DashboardSectionEmptyState className="dashboard-section-empty--embed" />
                ) : (
                  <div className="reports-efficiency-scorecard__table">
                    <ReportDataTable
                      key={
                        providerComparisonFullView.isFullView
                          ? 'eff-providers-fv'
                          : 'eff-providers-card'
                      }
                      columns={providerColumns}
                      rows={providerRows}
                      rowKey={(row) => row.id}
                      initialPageSize={5}
                      className={[
                        'reports-efficiency-scorecard__provider-table',
                        providerComparisonFullView.isFullView
                          ? 'reports-patient-value-revenue__provider-table--full-view'
                          : '',
                      ]
                        .filter(Boolean)
                        .join(' ')}
                      fullViewSectionId="eff-providers"
                      fullViewSearchWithFilter
                    />
                  </div>
                )}
              </div>
            </ReportSectionCard>
          </ReportExpandableSection>
        </div>
      </div>
    </div>
  )
}

export default EfficiencyScorecardReport
